<template>
  <div class="main-container">
    <AnalyticsHeader :selectedDepartments.sync="selectedDepartmentsId" />

    <AnalyticsOverview
      :selectedDepartments="selectedDepartmentsId"
      :resolvedCount="filteredResolvedCount"
      :inProgressCount="filteredInProgressCount"
      :abandonedCount="filteredAbandonedCount"
      :inQueueCount="filteredQueueChats.length"
      :averageWaitTime="filteredAverageWaitTimeString"
    />

    <div class="monitor-container">
      <AgentMonitor :selectedDepartments="selectedDepartmentsId" :activeTab="activeTab" />
      <ChatQueue
        :queueChats="filteredQueueChats"
        :averageWaitTime="filteredAverageWaitTime"
      />
    </div>
  </div>
</template>
<script>
import _ from 'lodash';
import { gql } from '@apollo/client/core';
import moment from 'moment-timezone';
import AnalyticsHeader from './AnalyticsHeader.vue';
import AnalyticsOverview from './AnalyticsOverview.vue';
import AgentMonitor from './AgentMonitor.vue';
import ChatQueue from './ChatQueue.vue';
import { liveAnalyticsMixin } from '@/mixins/liveAnalytics';

// Chat type constants
const CHAT_TYPES = {
  RESOLVED: 'resolved',
  ABANDONED: 'abandoned',
  IN_PROGRESS: 'inProgress',
  QUEUE: 'queue',
};

// Map chat types to data property names
const CHAT_ARRAY_MAP = {
  [CHAT_TYPES.RESOLVED]: 'resolvedChats',
  [CHAT_TYPES.ABANDONED]: 'abandonedChats',
  [CHAT_TYPES.IN_PROGRESS]: 'inProgressChats',
  [CHAT_TYPES.QUEUE]: 'queueChats',
};

export default {
  name: 'LiveAnalytics',
  mixins: [liveAnalyticsMixin],
  props: {
    activeTab: {
      type: String,
      default: '',
    },
  },
  components: {
    AnalyticsHeader,
    AnalyticsOverview,
    AgentMonitor,
    ChatQueue,
  },
  data() {
    return {
      resolvedChats: [],
      abandonedChats: [],
      inProgressChats: [],
      queueChats: [],
      departmentIndex: {},
      selectedDepartmentsId: [],
      lastResetDate: null,
      now: moment().local(),
      // Caches for memory optimization
      _deptCache: new Map(), // Cache department ID/name lookups
      _momentCache: new Map(), // Cache moment objects
    };
  },
  apollo: {
    $subscribe: {
      livechatResolved: {
        query: gql`subscription { livechatResolved }`,
        result({ data }) {
          const chat = data?.livechatResolved?.chat;
          if (!chat) return;

          const chatId = this.getChatId(chat);
          const hasAgents = chat.agents?.length > 0;
          const department = this.getChatDepartmentId(chat);

          if (hasAgents) {
            this.addChat(CHAT_TYPES.RESOLVED, chat, chatId, department);
            this.removeChat(CHAT_TYPES.IN_PROGRESS, chatId, department);
            this.updateAgentChatTracking('REMOVE', chat.agents, chatId);
          } else {
            this.removeChat(CHAT_TYPES.QUEUE, chatId, department);
            this.addChat(CHAT_TYPES.ABANDONED, chat, chatId, department);
          }
        },
      },
      livechatJoinQueue: {
        query: gql`subscription { livechatJoinQueue }`,
        result({ data }) {
          const chat = data?.livechatJoinQueue;
          if (!chat || chat.resolved) return;

          const chatId = this.getChatId(chat);
          const hasAgents = chat.agents?.length > 0;
          const department = this.getChatDepartmentId(chat);

          if (hasAgents) {
            this.removeChat(CHAT_TYPES.QUEUE, chatId, department);
            this.addChat(CHAT_TYPES.IN_PROGRESS, chat, chatId, department);
            this.updateAgentChatTracking('ADD', chat.agents, chatId);
          } else {
            this.addChat(CHAT_TYPES.QUEUE, chat, chatId, department);
          }
        },
      },
      livechatSessionUpdated: {
        query: gql`subscription { livechatSessionUpdated }`,
        result({ data }) {
          const chat = data?.livechatSessionUpdated;
          if (!chat || !chat.agents?.length || chat.resolved) return;

          const chatId = this.getChatId(chat);
          const department = this.getChatDepartmentId(chat);
          const wasInQueue = this.queueChats.some(
            (c) => this.getChatId(c) === chatId,
          );

          if (wasInQueue) {
            this.removeChat(CHAT_TYPES.QUEUE, chatId, department);
            this.addChat(CHAT_TYPES.IN_PROGRESS, chat, chatId, department);
            this.updateAgentChatTracking('ADD', chat.agents, chatId);
          }
        },
      },
      livechatSessionLeave: {
        query: gql`subscription { livechatSessionLeave }`,
        result({ data }) {
          const { previousChat, previousAgents = [] } = data?.livechatSessionLeave || {};
          if (!previousChat) return;

          const chatId = this.getChatId(previousChat);
          const department = this.getChatDepartmentId(previousChat);
          const hasAgents = previousChat.agents?.length > 0;
          const isResolved = previousChat.resolved;

          // Remove tracking for agents who left
          const leftAgents = previousAgents.filter(
            (agent) => !previousChat.agents?.includes(agent),
          );
          this.updateAgentChatTracking('REMOVE', leftAgents, chatId);

          if (!hasAgents && !isResolved) {
            this.removeChat(CHAT_TYPES.IN_PROGRESS, chatId, department);
            this.addChat(CHAT_TYPES.QUEUE, previousChat, chatId, department);
          }
        },
      },
    },
  },
  computed: {
    liveAnalyticsSettings() {
      return _.get(this.$store.state.modules, 'handover.liveChatLiveAnalytics', {});
    },
    autoResetEnabled() {
      return this.liveAnalyticsSettings?.autoReset === true;
    },
    resetAtTime() {
      return this.liveAnalyticsSettings?.resetAt || '00:00';
    },
    deptIds() {
      return this.selectedDepartmentsId.length > 0 ? this.selectedDepartmentsId.flat() : this.departments.flatMap(dept => dept.value);
    },
    deptNames() {
      return this.convertDepartmentIdsToNames(this.deptIds).map((v) => v.toLowerCase());
    },
    // Cached Sets to avoid repeated Set creation
    deptIdsSet() {
      // Guard against undefined deptIds (Vue timing issue)
      const ids = this.deptIds || [];
      return new Set(ids);
    },
    deptNamesSet() {
      // Guard against undefined deptNames (Vue timing issue)
      const names = this.deptNames || [];
      return new Set(names);
    },
    filteredQueueChats() {
      // Use cached Sets instead of creating new ones each time
      return this.queueChats.filter((chat) =>
        this.chatMatchesDepartments(chat, this.deptIdsSet, this.deptNamesSet),
      );
    },
    filteredResolvedCount() {
      return this.getFilteredCount(CHAT_TYPES.RESOLVED, this.resolvedChats);
    },
    filteredInProgressCount() {
      return this.getFilteredCount(CHAT_TYPES.IN_PROGRESS, this.inProgressChats);
    },
    filteredAbandonedCount() {
      return this.getFilteredCount(CHAT_TYPES.ABANDONED, this.abandonedChats);
    },
    filteredAverageWaitTime() {
      if (this.filteredQueueChats.length === 0) return 0;

      // Use cached moment objects
      const chatMoments = this.filteredQueueChats.map(chat =>
        this.getCachedMoment(chat.date_created)
      );

      const totalWaitMs = this.filteredQueueChats.reduce((sum, _chat, index) => {
        return sum + this.now.diff(chatMoments[index]);
      }, 0);

      return totalWaitMs / this.filteredQueueChats.length;
    },
    filteredAverageWaitTimeString() {
      return this.formatDuration(this.filteredAverageWaitTime);
    },
    noFilterSelected() {
      return !this.selectedDepartmentsId || this.selectedDepartmentsId.length === 0;
    },
  },
  methods: {
    chatMatchesDepartments(chat, deptIdsSet, deptNamesSet) {
      const chatDeptId = this.getCachedChatDepartmentId(chat);
      const chatDeptName = this.getRawDepartmentFromChat(chat);
      const generalDeptId = '00000000-0000-0000-0000-000000000000';

      // If chat has no department AND no filter is selected, include it
      const hasNoDepartment = !chatDeptId && !chatDeptName || (chatDeptId === generalDeptId && chatDeptName === generalDeptId);

      if (hasNoDepartment && this.noFilterSelected) {
        return true;
      }
      
      // If chat has a department ID that's in the selected IDs, include it
      if (chatDeptId && deptIdsSet.has(chatDeptId)) {
        return true;
      }

      return !!(chatDeptName && deptNamesSet.has(chatDeptName.toLowerCase()));
    },

    // Add chat to array and update department index
    addChat(type, chat, chatId, department) {
      const arrName = CHAT_ARRAY_MAP[type];
      const arr = this[arrName];

      if (!arr.some((c) => this.getChatId(c) === chatId)) {
        this[arrName] = [...arr, chat];
      }

      if (department) {
        if (!this.departmentIndex[department]) {
          this.$set(this.departmentIndex, department, {
            [CHAT_TYPES.RESOLVED]: new Set(),
            [CHAT_TYPES.ABANDONED]: new Set(),
            [CHAT_TYPES.IN_PROGRESS]: new Set(),
            [CHAT_TYPES.QUEUE]: new Set(),
          });
        }
        this.departmentIndex[department][type].add(chatId);
      }
    },

    // Remove chat from array and update department index
    removeChat(type, chatId, department) {
      const arrName = CHAT_ARRAY_MAP[type];
      this[arrName] = this[arrName].filter((c) => this.getChatId(c) !== chatId);

      if (department && this.departmentIndex[department]) {
        this.departmentIndex[department][type].delete(chatId);
      }
    },

    // Get filtered count using department index
    getFilteredCount(type, chats) {
      // Use cached Sets instead of creating new ones
      const deptIdsSet = this.deptIdsSet;
      const deptNamesSet = this.deptNamesSet;

      if (!this.selectedDepartmentsId?.length) {
        return chats.filter((chat) =>
          this.chatMatchesDepartments(chat, deptIdsSet, deptNamesSet),
        ).length;
      }

      // Filter selected - count chats in selected departments using department index
      const countFromIndex = this.getCountForDepartments(type, this.deptIds, this.departmentIndex);

      // Count chats that is not in index
      const chatsNotInIndex = chats.filter((chat) => {
        const chatDeptId = this.getCachedChatDepartmentId(chat);

        // Skip if in the index (already counted)
        if (chatDeptId && this.departmentIndex[chatDeptId]) {
          return false;
        }

        return this.chatMatchesDepartments(chat, deptIdsSet, deptNamesSet);
      });

      return countFromIndex + chatsNotInIndex.length;
    },

    // Cache moment objects to avoid repeated moment() calls
    getCachedMoment(dateString) {
      const MAX_CACHE_SIZE = 1000;

      if (!this._momentCache) this._momentCache = new Map();
  
      if (this._momentCache.size >= MAX_CACHE_SIZE) {
        const firstKey = this._momentCache.keys().next().value;
        this._momentCache.delete(firstKey);
      }
  
      if (!this._momentCache.has(dateString)) {
        this._momentCache.set(dateString, moment(dateString));
      }

      return this._momentCache.get(dateString);
    },

    // Initialize department cache for O(1) lookups
    initDepartmentCache() {
      const allDepartments = this.allDepartments;

      // Guard against undefined departments
      if (!allDepartments || !Array.isArray(allDepartments) || allDepartments.length === 0) {
        return;
      }

      // Initialize cache if it doesn't exist (Vue timing issue)
      if (!this._deptCache) {
        this._deptCache = new Map();
      }

      // Clear existing cache to avoid stale data
      this._deptCache.clear();

      // Build Map: name (lowercase) -> id
      allDepartments.forEach((dept) => {
        if (dept.name) {
          this._deptCache.set(dept.name.toLowerCase(), dept.id);
        }
        if (dept.id) {
          this._deptCache.set(dept.id, dept.id); // Cache ID -> ID for direct lookups
        }
      });
    },

    // Cached version of getChatDepartmentId - uses cache instead of array.find()
    getCachedChatDepartmentId(chat) {
      const rawDept = this.getRawDepartmentFromChat(chat);
      if (!rawDept) return null;

      // Check cache first - O(1) lookup
      if (this._deptCache.has(rawDept)) {
        return this._deptCache.get(rawDept);
      }

      // Fallback to original method if not in cache
      return this.getChatDepartmentId(chat);
    },

    // Update agent chat tracking in store
    updateAgentChatTracking(action, agents, chatId) {
      const mutation = action === 'ADD' ? 'ADD_AGENT_CHAT' : 'REMOVE_AGENT_CHAT';
      agents.forEach((agentEmail) => {
        this.$store.commit(`supervisor/${mutation}`, { agentEmail, chatId });
      });
    },

    async fetchAllChatsForAnalytics() {
      await Promise.all([
        this.$store.dispatch('supervisor/FETCH_RESOLVED_CHATS'),
        this.$store.dispatch('supervisor/FETCH_ABANDONED_CHATS'),
        this.$store.dispatch('supervisor/FETCH_IN_QUEUE_CHATS'),
        this.$store.dispatch('supervisor/FETCH_ON_GOING_CHATS'),
      ]);

      const state = this.$store.state.supervisor;
      const refetchActions = [
        ['FETCH_RESOLVED_CHATS', state.resolvedChatCount, state.resolvedChatsArray.length],
        ['FETCH_ABANDONED_CHATS', state.abandonedChatCount, state.abandonedChatsArray.length],
        ['FETCH_IN_QUEUE_CHATS', state.inQueueChatCount, state.inQueueChatsArray.length],
        ['FETCH_ON_GOING_CHATS', state.onGoingChatCount, state.onGoingChatsArray.length],
      ]
        .filter(([, totalCount, fetchedCount]) => totalCount > fetchedCount)
        .map(([action, totalCount]) =>
          this.$store.dispatch(`supervisor/${action}`, { limit: totalCount }),
        );

      if (refetchActions.length) {
        await Promise.all(refetchActions);
      }
    },

    // Fetch initial data from store
    async fetchInitialData() {
      try {
        await Promise.all([
          this.fetchAllChatsForAnalytics(),
          this.$store.dispatch('FETCH_AGENTS'),
          this.$store.dispatch('FETCH_DEPARTMENTS'),
        ]);

        // Initialize department cache after departments are loaded
        this.initDepartmentCache();

        const state = this.$store.state.supervisor;
        this.resolvedChats = [...(state.resolvedChatsArray || [])];
        this.abandonedChats = [...(state.abandonedChatsArray || [])];
        this.inProgressChats = [...(state.onGoingChatsArray || [])];
        this.queueChats = [...(state.inQueueChatsArray || [])];

        this.departmentIndex = this.buildDepartmentIndex({
          [CHAT_TYPES.RESOLVED]: this.resolvedChats,
          [CHAT_TYPES.ABANDONED]: this.abandonedChats,
          [CHAT_TYPES.IN_PROGRESS]: this.inProgressChats,
          [CHAT_TYPES.QUEUE]: this.queueChats,
        });

        this.$store.commit('supervisor/INIT_AGENT_CHATS', {
          chats: this.inProgressChats,
        });
      } catch {
        this.$notify.error({
          title: 'Error',
          message: 'Failed to load analytics data. Please refresh.',
        });
      }
    },

    checkAutoReset() {
      if (!this.autoResetEnabled) return;

      const now = moment().local();
      const currentTime = now.format('HH:mm');
      const today = now.format('YYYY-MM-DD');

      // Check if current time matches reset time and we haven't reset today
      if (currentTime === this.resetAtTime && this.lastResetDate !== today) {
        this.resetAnalytics();
        this.lastResetDate = today;
      }
    },
    resetAnalytics() {
      this.resolvedChats = [];
      this.abandonedChats = [];
      this.inProgressChats = [];
      this.queueChats = [];
      this.departmentIndex = {};
      this.$store.commit('supervisor/CLEAR_AGENT_CHATS');
      this.fetchInitialData();
    },
  },
  watch: {
    // Update department cache when departments change
    allDepartments: {
      handler() {
        this.initDepartmentCache();
      },
      deep: true,
    },
  },
  mounted() {
    this._timeInterval = setInterval(() => {
      this.now = moment().local();
      this.checkAutoReset();
    }, 1000);

    this.fetchInitialData();
  },
  beforeDestroy() {
    if (this._timeInterval) {
      clearInterval(this._timeInterval);
    }
    this.$store.commit('SET_NAVBAR_VISIBLE', true);
    this.$store.commit('supervisor/SET_LOCK_ON_LIVE_ANALYTICS', false);

    // Clean up caches to free memory
    this._deptCache.clear();
    this._momentCache.clear();
  },
};
</script>
<style lang="scss" scoped>
@import '../../../assets/scss/colors.scss';

.main-container {
  display: flex;
  flex-direction: column;
  min-height: 100vh;
  width: 100%;
  background: $color-light;
  gap: 2rem;
}

.monitor-container {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 2rem;
  padding: 0 1rem;

  @media only screen and (max-width: 850px) {
    grid-template-columns: repeat(1, 1fr);
    gap: 1rem 0rem;
  }
}
</style>
